import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Chevron } from 'src/components/ui/Features/Chevron/Chevron';
import ErrorPage from 'src/components/ui/Features/ErrorPage/ErrorPage';
import { IFrame } from 'src/components/ui/GlobalComponents/IFrame/IFrame';
import { useGuides } from 'src/hook/components/useGuides';
import { useGeneralSettings } from 'src/hook/data/useGeneralSettings';
import { useLearning } from 'src/hook/data/useLearning';
import useUser from 'src/hook/data/useUser';
import { useWords } from 'src/hook/data/useWords';
import { useGlobalMessageListener } from 'src/hook/global/useGlobalMessageListener';
import { useShare } from 'src/hook/global/useShare';
import { useComponentModal } from 'src/hook/modal/useComponentModal';
import usePopUp from 'src/hook/modal/usePopUp';
import { AnimationType, ChatTypes, ChevronType, Guides, IFrameMessage, PageSource } from 'src/model/enum/enums';
import { GetRazorSiteUrl, GetWebSiteUrl, IconLocation } from 'src/utils/URLUtils';
import styles from './Chat.module.scss';

export const Chat = () => {
    const { userInfo } = useUser();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const section = searchParams.get('section');
    const [query, setQuery] = useState<string>('');
    const { learningInfo } = useLearning();
    const [wordCounter, setWordCounter] = useState<number>(0);
    const { share } = useShare();
    const { open: openComponentModal } = useComponentModal();
    const { getWordById, getWordByText } = useWords();
    const { show } = usePopUp();
    const { checkWritingFeedbackLimit } = useGeneralSettings();
    const firstLoad = useRef(false);
    const { t } = useTranslation();
    const { showGuide } = useGuides();


    const getTitle = () => {
        if (section === ChatTypes.WordChat) {
            return `Let's Practice`;
        }
        if (section === ChatTypes.Writing) {
            return `Writing Practice`;
        }
        if (section === ChatTypes.GeneralChat) {
            return `Talk With Lexi`;
        }
        return '';
    }

    const handleShare = () => {
        share(`${GetWebSiteUrl()}`);
    };

    const getNextWordId = useCallback((counter: number) => {
        // Get words that are in UnknownWordIds but not in ToLearn or Review
        const unknownWordsNotInLearning = learningInfo?.UnknownWordIds.filter(wordId =>
            !learningInfo.ToLearn.includes(wordId) &&
            !learningInfo.ReviewWordIds.some(reviewId => parseInt(reviewId.split(':')[0]) === wordId)
        ) || [];

        // Get words from ReviewWordIds
        const reviewWords = learningInfo?.ReviewWordIds.map(id => parseInt(id.split(':')[0])) || [];

        // Get words from ToLearn
        const toLearnWords = learningInfo?.ToLearn || [];

        // Combine all word lists in priority order
        const allWords = [...unknownWordsNotInLearning, ...reviewWords, ...toLearnWords];

        // Return the current word ID or undefined if no words available
        return allWords.length > 0 ? allWords[counter % allWords.length] : undefined;

    }, [learningInfo]);

    const handleNextWord = useCallback((nextWordId?: number) => {
        if (!nextWordId) {
            setWordCounter(prev => prev + 1);
            nextWordId = getNextWordId(wordCounter + 1);
        }


        if (nextWordId !== undefined) {
            setQuery(`wordId=${nextWordId}`);
        } else {
            firstLoad.current = true;
            show({
                title: 'Please confirm',
                message: 'You don\'t have any words in your learning list.\n Do you want to discover more words?',
                primaryButtonText: 'Confirm',
                primaryButtonClick: () => navigate('/wordlists/discover?id=-1'),
                showSecondaryButton: true,
                secondaryButtonClick: () => navigate('/')
            });
        }
    }, [getNextWordId, wordCounter, setQuery, show, navigate]);

    const handleOpenDefinition = useCallback((url: string) => {
        try {
            const wordId = url.split('wordId=')[1];
            if (!wordId) return;
            const word = getWordById(parseInt(wordId));
            if (!word) return;
            openComponentModal('Definition', { word, source: PageSource.Chat }, AnimationType.SlideUp);
        } catch (error) {
            console.error('Error parsing definition URL:', error);
        }
    }, [getWordById, openComponentModal]);


    const handleOpenSmartWord = useCallback((wordText: string) => {
        try {
            const word = getWordByText(wordText);
            if (!word) return;
            openComponentModal('Definition', { word, source: PageSource.Chat }, AnimationType.SlideUp);
        } catch (error) {
            console.error('Error parsing definition URL:', error);
        }
    }, [getWordByText, openComponentModal]);

    const checkWritingFeedback = useCallback((updateCount: boolean = true) => {
        const canUseWritingFeedback = checkWritingFeedbackLimit(updateCount);
        if (!canUseWritingFeedback) {
            show({
                title: 'Writing feedback',
                message: 'Improve your English writing with real-time AI feedback tips. \n\n Advance your grammar and vocabulary usage with smart personalised AI tips!',
                primaryButtonText: 'See Plans',
                primaryButtonClick: () => navigate('/membership'),
                showSecondaryButton: true,
                secondaryButtonText: 'Later',
                showCloseButton: true,
                secondaryButtonClick: () => {
                    navigate('/');
                },
                onClose: () => {
                    navigate('/');
                }
            });
        }
    }, [checkWritingFeedbackLimit, show, navigate]);

    const handleMessage = useCallback((event: MessageEvent) => {
        if (event.data.type === IFrameMessage.NavigationToDomain) {
            const url = event.data.url;
            if (url.includes('defintion')) {
                handleOpenDefinition(url);
            }
            else {
                navigate(`/${url}`);
            }
        }
        else if (event.data.type === IFrameMessage.SmartWord) {
            const wordText = event.data.message;
            handleOpenSmartWord(wordText);
        }
        else if (event.data.type === IFrameMessage.WritingFeedback) {
            checkWritingFeedback(true);
        }
    }, [navigate, handleOpenDefinition, handleOpenSmartWord, checkWritingFeedback]);


    useGlobalMessageListener(handleMessage, [IFrameMessage.NavigationToDomain, IFrameMessage.SmartWord, IFrameMessage.WritingFeedback]);

    useEffect(() => {
        if (firstLoad.current || !learningInfo) return;

        const nextWordId = getNextWordId(wordCounter);


        if (section === ChatTypes.WordChat) {
            handleNextWord(nextWordId);
        }

        if (section === ChatTypes.GeneralChat || section === ChatTypes.WordChat) {
            if (nextWordId !== undefined) {
                firstLoad.current = true;
                showGuide(section === ChatTypes.GeneralChat ? Guides.ChatInput : Guides.ChatTitle);
            }
        }

        if (section === ChatTypes.Writing) {
            firstLoad.current = true;
            checkWritingFeedback(false);
        }
    }, [navigate, section, getNextWordId, show, learningInfo, showGuide, checkWritingFeedback, wordCounter, handleNextWord]);

    if (!section) return <ErrorPage message='Invalid URL' />;


    return (
        <div className={styles.chat}>
            <div className={styles.iframeHeader}>
                <Chevron type={ChevronType.Left} onClick={() => navigate('/')} />
                <h1>{t(getTitle())}</h1>
                {section === 'word-chat' && <button className={styles.nextButton} onClick={() => handleNextWord()}>{t('Next')}</button>}
                {section === 'general-chat' && (
                    <img
                        className={styles.icons}
                        src={IconLocation('share')}
                        alt="share"
                        onClick={handleShare}
                    />
                )}

            </div>
            <IFrame
                src={GetRazorSiteUrl(`practice/${section}`, userInfo?.Token, query)}
            />

        </div>
    );
}; 